import { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { ChevronLeft, ChevronRight, CreditCard, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge, EmptyState, Modal, Text } from '@/components/design-system'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import PlanFormFields, { blankPlanFormData, type PlanFormData } from './PlanFormFields'

interface PlanRow {
  id: string
  name: string
  slug: string
  description: string | null
  stripe_product_id: string | null
  stripe_price_id: string | null
  price_cents: number
  currency: string
  published: boolean
  position: number
  purchasable: boolean
  active_subscriptions_count: number
}

interface Pagination {
  page: number
  totalPages: number
  total: number
}

interface Props {
  plans: PlanRow[]
  pagination: Pagination
  search: string
}

function formatCurrency(cents: number, currency: string) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(cents / 100)
}

function planToForm(plan: PlanRow): PlanFormData {
  return {
    name: plan.name,
    slug: plan.slug,
    description: plan.description ?? '',
    stripe_product_id: plan.stripe_product_id ?? '',
    stripe_price_id: plan.stripe_price_id ?? '',
    price_cents: String(plan.price_cents),
    currency: plan.currency,
    published: plan.published ? 'true' : 'false',
    position: String(plan.position),
  }
}

export default function Index({ plans, pagination, search }: Props) {
  const [query, setQuery] = useState(search)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<PlanRow | null>(null)

  const createForm = useForm<PlanFormData>(blankPlanFormData())
  const editForm = useForm<PlanFormData>(blankPlanFormData())

  const setCreateData = (key: keyof PlanFormData, value: string) => {
    createForm.setData({ ...createForm.data, [key]: value })
  }

  const setEditData = (key: keyof PlanFormData, value: string) => {
    editForm.setData({ ...editForm.data, [key]: value })
  }

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault()
    router.get('/cc/plans', { search: query }, { preserveState: true })
  }

  const goToPage = (page: number) => router.get('/cc/plans', { search, page }, { preserveState: true })

  const closeCreateModal = () => {
    setCreateModalOpen(false)
    createForm.reset()
  }

  const handleCreate = () => {
    createForm.post('/cc/plans', { onSuccess: closeCreateModal })
  }

  const handleEdit = (plan: PlanRow) => {
    setSelectedPlan(plan)
    editForm.setData(planToForm(plan))
    setEditModalOpen(true)
  }

  const handleUpdate = () => {
    if (!selectedPlan) return
    editForm.patch(`/cc/plans/${selectedPlan.id}`, {
      onSuccess: () => {
        setEditModalOpen(false)
        setSelectedPlan(null)
      },
    })
  }

  const handleDeleteClick = (plan: PlanRow) => {
    setSelectedPlan(plan)
    setDeleteModalOpen(true)
  }

  const handleDelete = () => {
    if (!selectedPlan) return
    router.delete(`/cc/plans/${selectedPlan.id}`, {
      onSuccess: () => {
        setDeleteModalOpen(false)
        setSelectedPlan(null)
      },
    })
  }

  const hasNoPlansAtAll = plans.length === 0 && !search
  const hasNoSearchResults = plans.length === 0 && !!search
  const hasActiveSubscriptions = (selectedPlan?.active_subscriptions_count ?? 0) > 0

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <CreditCard className="h-6 w-6 text-muted-foreground" />
          <h2 className="text-2xl font-bold text-foreground">Planos de Cobrança</h2>
          <span className="text-sm text-muted-foreground">({pagination.total})</span>
        </div>
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar por nome, slug ou price..."
                aria-label="Buscar planos de cobrança"
                className="w-72 rounded-lg border border-border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <Button type="submit" variant="outline" size="sm">Buscar</Button>
          </form>
          <Button size="sm" onClick={() => setCreateModalOpen(true)}>
            <Plus className="h-4 w-4" />
            Novo Plano
          </Button>
        </div>
      </div>

      {hasNoPlansAtAll ? (
        <EmptyState
          title="Nenhum plano de cobrança"
          description="Crie um plano para habilitar checkout por colaborador digital."
          action={{ label: 'Novo Plano', onClick: () => setCreateModalOpen(true) }}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="max-h-[calc(100vh-220px)] overflow-auto">
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-border bg-muted">
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">Stripe</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">Preço</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">Assinaturas Ativas</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {plans.map((plan) => (
                  <tr key={plan.id} className="transition-colors hover:bg-accent">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-foreground">{plan.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{plan.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-foreground">{plan.stripe_price_id || 'sem price'}</div>
                      <div className="font-mono text-xs text-muted-foreground">{plan.stripe_product_id || 'sem product'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{formatCurrency(plan.price_cents, plan.currency)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        <Badge tone={plan.published ? 'success' : 'warning'}>{plan.published ? 'Publicado' : 'Oculto'}</Badge>
                        <Badge tone={plan.purchasable ? 'info' : 'attention'}>{plan.purchasable ? 'Checkout' : 'Sem checkout'}</Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={plan.active_subscriptions_count > 0 ? 'info' : undefined}>
                        {plan.active_subscriptions_count}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(plan)} aria-label={`Editar ${plan.name}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(plan)} aria-label={`Excluir ${plan.name}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {hasNoSearchResults && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhum plano encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border bg-muted px-4 py-3">
              <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.totalPages}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => goToPage(pagination.page - 1)} aria-label="Página anterior">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => goToPage(pagination.page + 1)} aria-label="Próxima página">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal
        open={createModalOpen}
        onClose={closeCreateModal}
        title="Novo Plano de Cobrança"
        primaryAction={{ content: 'Criar', onAction: handleCreate, loading: createForm.processing }}
        secondaryAction={{ content: 'Cancelar', onAction: closeCreateModal }}
        size="large"
      >
        <PlanFormFields data={createForm.data} errors={createForm.errors} setData={setCreateData} />
      </Modal>

      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Editar Plano de Cobrança"
        primaryAction={{ content: 'Salvar', onAction: handleUpdate, loading: editForm.processing }}
        secondaryAction={{ content: 'Cancelar', onAction: () => setEditModalOpen(false) }}
        size="large"
      >
        <PlanFormFields data={editForm.data} errors={editForm.errors} setData={setEditData} />
      </Modal>

      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Excluir Plano de Cobrança"
        destructiveAction={{ content: 'Excluir', onAction: handleDelete, disabled: hasActiveSubscriptions }}
        secondaryAction={{ content: 'Cancelar', onAction: () => setDeleteModalOpen(false) }}
      >
        <Text>Tem certeza que deseja excluir o plano <strong>{selectedPlan?.name}</strong>?</Text>
        {hasActiveSubscriptions && (
          <p className="mt-2 text-sm text-red-600">
            Este plano possui {selectedPlan?.active_subscriptions_count} assinatura(s) ativa(s).
          </p>
        )}
      </Modal>
    </div>
  )
}

Index.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>
